import type { UseFinancingData } from '../financings/useFinancing'
import type { AcquisitionKit, HardwareBodySave } from '~/utils/installation/Installation'
import type { EquipmentOrigin, ReceiptModel } from '~/utils/receipt-model/ReceiptModel'
import { useMutation } from '@tanstack/vue-query'
import queryClient, { queryKeys } from '~/queryClient'
import { saveHardware } from '~/services-v2/installation-api/installation-api'
import { getPaymentModel } from '~/services-v2/payment-model-api/payment-model-api'
import promises from '~/utils/promises'

const RECEIPT_REFETCH_MAX_ATTEMPTS = 8
const RECEIPT_REFETCH_MIN_DELAY_MS = 2000
const RECEIPT_REFETCH_MAX_DELAY_MS = 5000

function receiptRefetchDelayMs(attempt: number) {
  return Math.min(RECEIPT_REFETCH_MIN_DELAY_MS + attempt * 1000, RECEIPT_REFETCH_MAX_DELAY_MS)
}

function applyExpectedOrigin(projectId: string, expectedOrigin: EquipmentOrigin) {
  queryClient.setQueryData<ReceiptModel>(queryKeys.PAYMENT_MODEL.DATA(projectId), (oldData) => {
    if (!oldData)
      return oldData

    return {
      ...oldData,
      equipment_origin: expectedOrigin,
    }
  })
}

async function refetchReceiptModelAfterHardwareSave(
  projectId: string,
  expectedKit?: AcquisitionKit,
) {
  const queryKey = queryKeys.PAYMENT_MODEL.DATA(projectId)

  if (expectedKit)
    applyExpectedOrigin(projectId, expectedKit)

  for (let attempt = 0; attempt < RECEIPT_REFETCH_MAX_ATTEMPTS; attempt++) {
    await promises.wait(receiptRefetchDelayMs(attempt))

    try {
      const receipt = await getPaymentModel(projectId)
      if (!expectedKit || receipt.equipment_origin === expectedKit) {
        queryClient.setQueryData<ReceiptModel>(queryKey, receipt)
        return
      }
    }
    catch {
      // GET falhou; tenta de novo até o limite. A origem otimista permanece no cache.
    }
  }
}

export default function useSaveHardware(projectId) {
  const mutationFn = async (hardwareBodySave: HardwareBodySave) => {
    const response = await saveHardware(hardwareBodySave)
    return response
  }

  return useMutation({
    mutationFn,
    mutationKey: queryKeys.INSTALLATION.SAVE_HARDWARE(projectId),
    // 422 = erro de negócio → não faz retry; demais falhas (5xx/rede) tentam até 2x.
    retry: (failureCount: number, error: any) => {
      const status = error?.response?.status ?? error?.status
      if (status === 422)
        return false
      return failureCount < 2
    },
    retryDelay: 1000,
    onSuccess: (_data, variables) => {
      const financingQueryKey = queryKeys.FINANCING.DETAILS(projectId.value)

      queryClient.setQueryData<UseFinancingData>(financingQueryKey, (oldData) => {
        if (!oldData)
          return oldData

        return {
          ...oldData,
          reason: undefined,
          section_statuses: {
            ...oldData.section_statuses,
            equipment: {
              ...oldData.section_statuses.equipment,
              status: 'under_analysis',
            },
          },
        }
      })

      void Promise.all([
        queryClient.refetchQueries({ queryKey: financingQueryKey }),
        queryClient.refetchQueries({ queryKey: queryKeys.INSTALLATION.DETAILS(projectId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.HARDWARE.FORM(projectId.value) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.PROJECT.DETAILS(projectId.value) }),
        refetchReceiptModelAfterHardwareSave(variables.project_id, variables.selected_aquisition_kit),
      ])
    },
  })
}
